/** Inbox presentation rules: filters, row icons, previews and relative times (R72, D66). Pure, no DOM. */

export const MAIL_FILTERS = ["all", "battle", "reward", "system"] as const;
export type MailFilter = (typeof MAIL_FILTERS)[number];

/** The mail kinds behind a filter; `null` means every kind. */
export function filterKinds(filter: MailFilter): string[] | null {
  if (filter === "all") return null;
  if (filter === "system") return ["system", "receipt", "admin"];
  return [filter];
}

export type MailIcon = "battle-won" | "battle-lost" | "reward" | "system";

export interface MailRowInput {
  kind: string;
  subject: string;
  body: string;
  rewards: unknown | null;
  ref: string | null;
  claimed: boolean;
  outcome: "won" | "lost" | null;
}

export interface MailRowView {
  icon: MailIcon;
  title: string;
  preview: string;
  claimable: boolean;
  /** A battle report exists for this mail (raid, expedition, arena…). */
  watch: boolean;
}

/** Mails carry "DE / EN" in one string; the row shows the half for the current locale. */
function localeHalf(text: string, locale: "de" | "en"): string {
  const parts = text.split(" / ");
  if (parts.length < 2) return text;
  return locale === "de" ? parts[0]! : parts.slice(1).join(" / ");
}

export function mailRow(m: MailRowInput, locale: "de" | "en"): MailRowView {
  const icon: MailIcon = m.kind === "battle" ? (m.outcome === "lost" ? "battle-lost" : "battle-won") : m.kind === "reward" || m.rewards ? "reward" : "system";
  const firstLine = m.body.split("\n")[0] ?? "";
  return {
    icon,
    title: localeHalf(m.subject, locale),
    preview: localeHalf(firstLine, locale),
    claimable: m.rewards !== null && !m.claimed,
    watch: typeof m.ref === "string" && m.ref.startsWith("btl_"),
  };
}

/** "gerade eben", "5 min ago", "vor 3 Std.", "2 d ago": coarse, honest, no clock drift games. */
export function relativeTime(at: number, now: number, locale: "de" | "en"): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return locale === "de" ? "gerade eben" : "just now";
  const m = Math.round(s / 60);
  if (m < 60) return locale === "de" ? `vor ${m} Min.` : `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return locale === "de" ? `vor ${h} Std.` : `${h} h ago`;
  const d = Math.round(h / 24);
  return locale === "de" ? `vor ${d} T.` : `${d} d ago`;
}
